import { createContext } from 'react';

/**
 * The Orders permission codes, exactly as the shared new-auth-server holds
 * them (orders-server/db/auth_permissions_seed.sql) and the orders server
 * checks them (orders-server/src/lib/permissions.js). docs/PERMISSIONS.md is
 * the contract; this union is the frontend's copy of it, so a code that is
 * not in the catalog cannot be named here at all.
 *
 * Three tiers:
 *   page:*   which screens open — a nav row, its route, its overview rows.
 *            Enforced here; the server has no pages.
 *   read:*   which data the server hands out. `read:journeys` is the base
 *            every Orders user needs; the other three are the sensitive sets
 *            and the server nulls or refuses their data without them.
 *   write:*  which mutations the server accepts, one code per feature. A
 *            control is rendered only when its code is held.
 */
export type PagePermission =
  | 'orders:page:overview'
  | 'orders:page:journeys'
  | 'orders:page:to_order'
  | 'orders:page:setup'
  | 'orders:page:releases'
  | 'orders:page:actions'
  | 'orders:page:discrepancies'
  | 'orders:page:move'
  | 'orders:page:documents'
  | 'orders:page:balance'
  | 'orders:page:duties'
  | 'orders:page:prices'
  | 'orders:page:followups'
  | 'orders:page:packages'
  | 'orders:page:count_sheet'
  | 'orders:page:warehouse';

export type ReadPermission =
  | 'orders:read:journeys'
  | 'orders:read:costs'
  | 'orders:read:drafts'
  | 'orders:read:mail';

export type WritePermission =
  | 'orders:write:journeys'
  | 'orders:write:lifecycle'
  | 'orders:write:documents'
  | 'orders:write:nudges'
  | 'orders:write:actions'
  | 'orders:write:drafts'
  | 'orders:write:balance'
  | 'orders:write:duties'
  | 'orders:write:prices'
  | 'orders:write:packages';

/** One permission code from the shared new-auth-server (colon style). */
export type PermissionCode = PagePermission | ReadPermission | WritePermission | 'orders:agent';

export interface AuthUser {
  id: number;
  username: string;
  email?: string | null;
  is_admin: boolean;
  permissions: string[];
}

export interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  /** True while the boot-time `/auth/me` is in flight. */
  isLoading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  /**
   * Does the signed-in user hold this permission? Admins hold everything, and
   * so does the `orders:agent` service identity (the server middleware treats
   * it as a wildcard, so the UI must agree). The server is the real gate —
   * this only decides whether a control is rendered at all.
   */
  can: (code: PermissionCode) => boolean;
}

export const AuthContext = createContext<AuthContextValue | null>(null);
